/**
 * Une couleur telle que le color picker (Ui::ColorPickerComponent) la lit et l'écrit : une
 * valeur CSS, dans un input comme n'importe quelle autre.
 *
 *  - "transparent"
 *  - "#rrggbb", ou "rgba(r,g,b,a)" quand la couleur porte une opacité
 *  - "linear-gradient(<angle>deg, <couleur> <position>%, …)" — N arrêts, l'angle en degrés
 *    CSS (0° du bas vers le haut, 90° de gauche à droite)
 *
 * Et ses traductions vers et depuis le remplissage d'un objet fabric : une string, ou un
 * Gradient sérialisé dont les coordonnées suivent la convention de la lib (0° de gauche à
 * droite — un quart de tour de moins que le CSS).
 */

export interface GradientStop {
  color: string
  /** De 0 à 1. */
  offset: number
}

export interface GradientValue {
  stops: GradientStop[]
  /** En degrés CSS. */
  angle: number
}

export type ColorValue =
  | { kind: "transparent" }
  | { kind: "solid"; color: string }
  | { kind: "gradient"; gradient: GradientValue };

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
const RGBA = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([0-9.]+))?\s*\)$/i;
const GRADIENT = /^linear-gradient\((.*)\)$/is;
const STOP = /^(.*?)(?:\s+(-?[0-9.]+)%)?$/s;

/** Les morceaux d'une liste CSS, séparés par `sep` hors parenthèses. */
export function splitTopLevel(value: string, sep = ","): string[] {
  const parts: string[] = [];
  let depth = 0;
  let current = "";
  for (const char of value) {
    if (char === "(") depth += 1;
    if (char === ")") depth -= 1;
    if (char === sep && depth === 0) {
      parts.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  if (current.trim()) parts.push(current.trim());
  return parts;
}

export function parseColorValue(value: string | null | undefined): ColorValue {
  const css = (value ?? "").trim();
  if (!css || css === "transparent") return { kind: "transparent" };
  const gradient = css.match(GRADIENT);
  if (!gradient) return { kind: "solid", color: css };

  const parts = splitTopLevel(gradient[1]);
  let angle = 180;
  if (/^-?[0-9.]+deg$/.test(parts[0] ?? "")) angle = Math.round(parseFloat(parts.shift()!));
  const stops = parts.map((part, index) => {
    const [, color, offset] = part.match(STOP)!;
    return {
      color: color.trim(),
      offset: offset === undefined ? (parts.length === 1 ? 0 : index / (parts.length - 1)) : parseFloat(offset) / 100,
    };
  });
  if (stops.length < 2) return { kind: "solid", color: stops[0]?.color ?? "#000000" };
  return { kind: "gradient", gradient: { stops, angle: ((angle % 360) + 360) % 360 } };
}

export function gradientCss({ stops, angle }: GradientValue): string {
  const list = stops.map(({ color, offset }) => `${color} ${Math.round(offset * 100)}%`).join(", ");
  return `linear-gradient(${Math.round(angle)}deg, ${list})`;
}

export function colorValueCss(value: ColorValue): string {
  if (value.kind === "transparent") return "transparent";
  if (value.kind === "solid") return value.color;
  return gradientCss(value.gradient);
}

/** Les arrêts répartis à égale distance, dans l'ordre donné (la v1 n'a pas de poignées). */
export function equidistant(stops: GradientStop[]): GradientStop[] {
  const last = Math.max(stops.length - 1, 1);
  return stops.map((stop, index) => ({ ...stop, offset: index / last }));
}

// ── Couleur et opacité ──────────────────────────────────────────────────────

/** "#rrggbb" et l'alpha (0..1) d'une couleur simple ; null pour tout le reste. */
export function parseColor(color: string): { hex: string; alpha: number } | null {
  const value = color.trim();
  if (HEX.test(value)) {
    const raw = value.slice(1);
    const hex = raw.length === 3 ? raw.split("").map((c) => c + c).join("") : raw;
    return { hex: `#${hex.toLowerCase()}`, alpha: 1 };
  }
  const rgba = value.match(RGBA);
  if (!rgba) return null;
  const channel = (n: string) => Math.max(0, Math.min(255, parseInt(n, 10))).toString(16).padStart(2, "0");
  return { hex: `#${channel(rgba[1])}${channel(rgba[2])}${channel(rgba[3])}`, alpha: rgba[4] === undefined ? 1 : parseFloat(rgba[4]) };
}

/** Un hex avec une opacité : l'hex tel quel à 1, un rgba() sinon. */
export function withAlpha(hex: string, alpha: number): string {
  if (alpha >= 1) return hex;
  const r = parseInt(hex.slice(1, 3), 16) || 0;
  const g = parseInt(hex.slice(3, 5), 16) || 0;
  const b = parseInt(hex.slice(5, 7), 16) || 0;
  return `rgba(${r},${g},${b},${Math.max(0, alpha).toFixed(2).replace(/\.?0+$/, "")})`;
}

// ── fabric ──────────────────────────────────────────────────────────────────

export const cssAngleToLib = (deg: number): number => ((deg - 90) % 360 + 360) % 360;
export const libAngleToCss = (deg: number): number => ((deg + 90) % 360 + 360) % 360;

/** Un dégradé en objet Gradient sérialisé (coordonnées relatives) : ce que la lib charge. */
export function fabricGradient({ stops, angle }: GradientValue): object {
  const rad = (cssAngleToLib(angle) * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return {
    type: "linear",
    gradientUnits: "percentage",
    coords: { x1: 0.5 - cos / 2, y1: 0.5 - sin / 2, x2: 0.5 + cos / 2, y2: 0.5 + sin / 2 },
    colorStops: stops.map(({ color, offset }) => ({ color, offset })),
  };
}

/** Le remplissage d'un objet fabric (string, ou Gradient sérialisé) en valeur CSS. */
export function fabricFillToCss(fill: unknown): string {
  if (!fill) return "transparent";
  if (typeof fill === "string") return fill;
  const { colorStops, coords } = fill as { colorStops?: GradientStop[]; coords?: Record<string, number> };
  if (!colorStops?.length) return "transparent";
  const { x1 = 0, y1 = 0, x2 = 1, y2 = 0 } = coords || {};
  const angle = libAngleToCss(Math.round((Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI));
  return gradientCss({ stops: colorStops, angle });
}
